import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, Package, Mail, ArrowRight } from 'lucide-react'
import { useCart } from '../context/CartContext'
import Button from '../components/ui/Button'

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams()
  const { clearCart } = useCart()
  const [orderDetails, setOrderDetails] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    // Clear the cart on successful checkout
    clearCart()

    if (sessionId) {
      fetchOrderDetails()
    } else {
      setIsLoading(false)
    }
  }, [sessionId])

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(
        `/.netlify/functions/get-order?session_id=${sessionId}`
      )
      const data = await response.json()
      setOrderDetails(data)
    } catch (error) {
      console.error('Error fetching order details:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  return (
    <div className="min-h-screen bg-pp-ivory">
      <div className="container mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-xl mx-auto text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-8 rounded-full bg-pp-sage/20 flex items-center justify-center"
          >
            <CheckCircle className="w-10 h-10 text-pp-sage" />
          </motion.div>

          <h1 className="font-display text-display-md text-pp-ink mb-4">
            Thank You!
          </h1>
          <p className="text-pp-stone text-lg mb-8">
            Your order has been placed successfully. We're preparing your little
            one's new treasures!
          </p>

          {/* Order Details */}
          {isLoading ? (
            <div className="animate-pulse text-pp-stone">
              Loading order details...
            </div>
          ) : orderDetails ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 border border-pp-sand/50 text-left mb-8"
            >
              {orderDetails.order_number && (
                <div className="mb-4 pb-4 border-b border-pp-sand/50">
                  <p className="text-sm text-pp-stone">Order Number</p>
                  <p className="font-medium text-pp-ink text-lg">
                    {orderDetails.order_number}
                  </p>
                </div>
              )}

              <div className="space-y-3 mb-4">
                {(orderDetails.order_items || orderDetails.items || []).map(
                  (item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-pp-charcoal">
                        {item.product_name || item.name} × {item.quantity}
                      </span>
                      <span className="text-pp-ink">
                        {formatPrice(
                          item.total_price || item.price * item.quantity
                        )}
                      </span>
                    </div>
                  )
                )}
              </div>

              <div className="pt-4 border-t border-pp-sand/50">
                <div className="flex justify-between font-medium">
                  <span className="text-pp-ink">Total</span>
                  <span className="text-pp-ink">
                    {formatPrice(orderDetails.total)}
                  </span>
                </div>
              </div>
            </motion.div>
          ) : null}

          {/* What's Next */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-pp-linen rounded-2xl p-6 mb-8"
          >
            <h2 className="font-display text-lg text-pp-ink mb-4">
              What happens next?
            </h2>
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-pp-terracotta" />
                </div>
                <div>
                  <p className="font-medium text-pp-ink">Confirmation Email</p>
                  <p className="text-sm text-pp-stone">
                    You'll receive an order confirmation email shortly with all
                    the details.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-pp-sage" />
                </div>
                <div>
                  <p className="font-medium text-pp-ink">Shipping Update</p>
                  <p className="text-sm text-pp-stone">
                    We'll send you tracking information once your order ships
                    (usually within 1 business day).
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button as={Link} to="/shop">
              Continue Shopping
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button as={Link} to="/account/orders" variant="outline">
              View Order History
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
